import { useRegisterAgentContext } from '@backrs_ui/grow-ui/src/agentContext/useRegisterAgentContext';
import {
  COMPARE_FACTORS,
  type ScoredComparison,
} from '@backrs_ui/grow-ui/src/redux/userColleges/userColleges.types';

import { percent, topMatchId } from './compare.util';

type CompareAgentContext = Readonly<{
  comparison: ScoredComparison | undefined;
  saved: Readonly<{ id: number; name: string }> | undefined;
  view: 'heatMap' | 'cards';
  weightsOpen: boolean;
  invitedToSetWeights: boolean;
}>;

export const useAgentContext = ({
  comparison,
  saved,
  view,
  weightsOpen,
  invitedToSetWeights,
}: CompareAgentContext) => {
  const topId = comparison ? topMatchId(comparison.colleges) : null;

  useRegisterAgentContext(
    comparison
      ? {
          kind: 'visual',
          label: saved
            ? `Saved college comparison "${saved.name}" (id ${saved.id})`
            : 'Unsaved college comparison',
          display: `${view === 'heatMap' ? 'A heat map with factors down and colleges across, best match first' : 'One card per college listing every factor, best match first'}. Each fact is shaded Strong (fit 4-5), Mid (3) or Weak (1-2); each college shows its match percentage, the top match is outlined with a Top Match badge, and a Best for / Watch note names its strongest and weakest factor. Each factor shows its share of the score; clicking it opens that factor's weight. Colleges can be on the student's list or not; one that isn't has a plus button to add it. Changes are only kept once the student presses Save (or Update saved list).${weightsOpen ? ' The Edit Weights dialog is open: the student picks a factor from a dropdown and sets how much it matters on a slider (plus ranking for type, size and setting, or the acceptable distance).' : ''}${invitedToSetWeights ? ' A Before you begin banner invites the student to tell Lubav what matters to them; its button starts a short chat that saves their own weights for every comparison and rescores this one as they answer.' : ''}`,
          detail:
            "Match = sum of importance x fit over sum of importances x 5, over the factors with data; a fact with no data is left out of that college's score. Net cost, graduation rate, earnings and ROI fit by national quintile among 4-year schools. The get-college-comparison tool reads a saved comparison by its id; an unsaved one exists only on this screen.",
          data: {
            distance_that_is_fine: comparison.settings.distance,
            importances: Object.fromEntries(
              COMPARE_FACTORS.map((factor) => [
                factor,
                {
                  importance: comparison.settings.importances[factor],
                  percent: percent(comparison.weights[factor]),
                },
              ]),
            ),
            preferences: comparison.settings.preferences,
            colleges: comparison.colleges.map((college) => ({
              name: college.name,
              match_percent: college.match === null ? null : percent(college.match),
              top_match: college.institution_id === topId,
              on_my_college_list: college.user_college_id !== null,
              net_cost_source: college.net_cost_source,
              factors: college.factors,
              best_at: college.best_at,
              watch: college.watch,
            })),
          },
        }
      : null,
  );
};
